/**
 * Occupational health API client (/api/occupational-health/*).
 *
 * The module holds only what the EHS MIS reports: who, when, next due, fitness
 * outcome; and for illness, category, work-relatedness, notification, days lost.
 * There is NO diagnosis, findings or restrictions field — the server does not
 * collect them, so there is nothing here to render.
 *
 * TWO LEVELS, and the client must not blur them:
 *   - `fetchHealthSummary` / `fetchHealthInsights` return AGGREGATES. Safe for
 *     management; category cells below the suppression threshold arrive as the
 *     string "<5", or "—" when the whole group is too small for any outcome to be
 *     published.
 *   - everything under /records/* returns individual records. The server requires
 *     the dedicated `occupational_health` grant — there is no admin shortcut —
 *     and audits every read.
 *
 * Call `fetchHealthMeta` first: its `record_access` flags say whether to offer the
 * record lists at all. Hiding them is courtesy, not security — the endpoints
 * enforce access regardless of what this client renders.
 *
 * Suppressed values are STRINGS by design. Never coerce them with Number() to
 * "clean up" a table: that turns a withheld cell into 0 or NaN and reintroduces
 * exactly the disclosure the suppression prevents.
 */
import axios from 'axios';

const base = () => `${process.env.REACT_APP_BACKEND_URL}/api/occupational-health`;

/** True when a cell was withheld rather than measured. */
export const isSuppressed = (value) => typeof value === 'string';

export const FITNESS_LABELS = {
  fit: 'Fit',
  fit_with_restrictions: 'Fit with restrictions',
  temporarily_unfit: 'Temporarily unfit',
  unfit: 'Unfit',
  pending: 'Awaiting outcome',
};

export const EXAM_TYPE_LABELS = {
  pre_employment: 'Pre-employment',
  periodic: 'Periodic',
  post_illness: 'Post-illness',
  return_to_work: 'Return to work',
  exit: 'Exit',
  special: 'Special',
};

/** Controlled vocabularies plus this caller's clinical access. */
export async function fetchHealthMeta({ signal } = {}) {
  const { data } = await axios.get(`${base()}/meta`, { signal });
  return data || {};
}

/** Aggregates for management. No individual, no clinical text. */
export async function fetchHealthSummary({ siteId } = {}, { signal } = {}) {
  const params = {};
  if (siteId) params.site_id = siteId;
  const { data } = await axios.get(`${base()}/summary`, { params, signal });
  return data || {};
}

/** Who is due or overdue for examination — the recall list. */
export async function fetchWorklist({ siteId, limit = 200 } = {}, { signal } = {}) {
  const params = { limit };
  if (siteId) params.site_id = siteId;
  const { data } = await axios.get(`${base()}/records/worklist`, { params, signal });
  return data?.worklist || [];
}

/** Examination records. */
export async function fetchExaminations(
  { siteId, employeeCode, examType, fitnessStatus, limit = 200 } = {}, { signal } = {},
) {
  const params = { limit };
  if (siteId) params.site_id = siteId;
  if (employeeCode) params.employee_code = employeeCode;
  if (examType) params.exam_type = examType;
  if (fitnessStatus) params.fitness_status = fitnessStatus;
  const { data } = await axios.get(`${base()}/records/examinations`, { params, signal });
  return data?.examinations || [];
}

export async function createExamination(body) {
  const { data } = await axios.post(`${base()}/records/examinations`, body);
  return data;
}

export async function updateExamination(id, body) {
  const { data } = await axios.put(`${base()}/records/examinations/${id}`, body);
  return data;
}

export async function deleteExamination(id, { siteId } = {}) {
  const params = {};
  if (siteId) params.site_id = siteId;
  const { data } = await axios.delete(`${base()}/records/examinations/${id}`, { params });
  return data;
}

/** Occupational illness cases. */
export async function fetchIllnessCases(
  { siteId, status, limit = 200 } = {}, { signal } = {},
) {
  const params = { limit };
  if (siteId) params.site_id = siteId;
  if (status) params.status = status;
  const { data } = await axios.get(`${base()}/records/illness-cases`, { params, signal });
  return data?.cases || [];
}

export async function createIllnessCase(body) {
  const { data } = await axios.post(`${base()}/records/illness-cases`, body);
  return data;
}

export async function updateIllnessCase(id, body) {
  const { data } = await axios.put(`${base()}/records/illness-cases/${id}`, body);
  return data;
}

export async function deleteIllnessCase(id, { siteId } = {}) {
  const params = {};
  if (siteId) params.site_id = siteId;
  const { data } = await axios.delete(`${base()}/records/illness-cases/${id}`, { params });
  return data;
}

/**
 * Ranked findings and the department breakdown for one factory. Aggregates only,
 * so this needs no record access — it is what a plant head reads.
 */
export async function fetchHealthInsights({ siteId } = {}, { signal } = {}) {
  const params = {};
  if (siteId) params.site_id = siteId;
  const { data } = await axios.get(`${base()}/insights`, { params, signal });
  return data || {};
}

/**
 * Download the CSV template HR fills in. `kind` is 'examinations' or 'illness'.
 * Returns { blob, filename }.
 */
export async function downloadRecordTemplate({ kind = 'examinations' } = {}) {
  const resp = await axios.get(`${base()}/records/template`,
    { params: { kind }, responseType: 'blob' });
  const cd = resp.headers['content-disposition'] || '';
  const match = /filename="?([^"]+)"?/.exec(cd);
  return {
    blob: resp.data,
    filename: match ? match[1] : `occupational_health_${kind}_template.csv`,
  };
}

/**
 * Upload a filled template. The server validates the WHOLE file first and writes
 * nothing if any row is wrong, so a rejection leaves the register untouched — the
 * error list comes back in `error.response.data.detail.errors` with row numbers.
 */
export async function importRecords({ siteId, kind = 'examinations', file }) {
  const form = new FormData();
  form.append('file', file);
  const { data } = await axios.post(`${base()}/records/import`, form, {
    params: { kind, site_id: siteId },
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data || {};
}
